import { CalendarProps, DateInputProps, InputProps, TextAreaProps } from "ds/ui";
import { DatePickerProps } from "@heroui/react";
import clsx from "clsx";

type InputSize = "sm" | "md" | "lg";

export const getInputClassNames = (
  size?: InputSize,
): InputProps["classNames"] => ({
  base: ["opacity-100"],
  description: ["text-gray-600"],
  errorMessage: ["text-danger-dark"],
  // Make each helper (description or error) a row and push last <span> (the counter) right
  helperWrapper: [
    // remove HeroUI default padding
    "p-0",

    "[&>[data-slot=description]:not(:empty)]:flex",
    "[&>[data-slot=description]:not(:empty)]:items-center",
    "[&>[data-slot=description]:not(:empty)]:p-1",
    "[&>[data-slot=description]:not(:empty)>span:last-child]:ml-auto",

    "[&>[data-slot=error-message]:not(:empty)]:flex",
    "[&>[data-slot=error-message]:not(:empty)]:items-center",
    "[&>[data-slot=error-message]:not(:empty)]:p-1",
    "[&>[data-slot=error-message]:not(:empty)>span:last-child]:ml-auto",
  ],
  innerWrapper: [
    clsx({
      "[&>svg]:w-4": size === "sm",
      "[&>svg]:w-5": size === "md",
      "[&>svg]:w-6": size === "lg",
    }),
    "[&>svg]:stroke-sm!",
  ],
  input: [
    "text-gray-900! placeholder:text-gray-600 group-data-[hover=true]:placeholder:text-gray-900",
    "data-[has-start-content=true]:ps-2 data-[has-end-content=true]:pe-2",
    "group-data-[invalid=true]:placeholder:text-danger",
  ],
  inputWrapper: [
    clsx({
      "px-3": size === "sm",
      "px-4": size === "md" || size === "lg",
    }),
    "outline-hidden!",
    "shadow-none rounded-lg border-0 bg-white ring-1 ring-inset ring-gray-500 data-[disabled=true]:ring-gray-300",
    "data-[hover=true]:bg-white data-[hover=true]:ring-black group-data-[invalid=true]:hover:ring-danger-dark",
    "group-data-[invalid=true]:ring-danger group-data-[invalid=true]:text-danger",
    "group-data-[disabled=true]:bg-gray-200 group-data-[disabled=true]:text-gray-disabled",
    "group-data-[focus=true]:bg-white group-data-[focus=true]:ring-primary group-data-[focus=true]:ring-2",
  ],
  label: [
    clsx({
      "start-3 text-sm": size === "sm",
      "start-4": size === "md" || size === "lg",
    }),
    "text-default-900! font-semibold",
    "[&::after]:text-black",
  ],
});

export const getTextareaClassNames = (
  size?: InputSize,
): TextAreaProps["classNames"] => {
  const inputClassNames = getInputClassNames(size);

  return {
    ...inputClassNames,
    label: [
      clsx({
        "text-medium": size === "lg",
        "text-sm": size === "sm",
        "text-small": size === "md",
      }),
      "text-default-900! font-semibold",
      "after:text-gray-900!",
      "[&::after]:text-black",
    ],
  };
};

export const getDateInputClassNames = (
  size?: InputSize,
): DateInputProps["classNames"] => ({
  base: "group flex flex-col opacity-100 data-[has-helper=true]:pb-8",
  description: ["text-tiny text-gray-600"],
  errorMessage: ["text-danger-dark"],
  helperWrapper: "",
  innerWrapper: [
    "flex items-center text-gray-900 w-full gap-x-2 h-6",
    clsx({
      "[&>svg]:w-4": size === "sm",
      "[&>svg]:w-5": size === "md",
      "[&>svg]:w-6": size === "lg",
    }),
    "[&>svg]:stroke-sm!",
    "group-data-[invalid=true]:text-danger",
  ],
  input: [
    "caret-current placeholder:text-gray-600",
    "data-[has-start-content=true]:ps-2 data-[has-end-content=true]:pe-2",
    "group-data-[invalid=true]:placeholder:text-danger",
    "focus-visible:outline-hidden!",
  ],
  inputWrapper: [
    "px-3 gap-3 w-full inline-flex flex-row items-center",
    "cursor-text tap-highlight-transparent shadow-none",
    "data-focus-visible:outline-2! data-focus-visible:outline-focus!",
    "shadow-none rounded-lg border-0 bg-white ring-1 ring-inset ring-gray-500 data-[disabled=true]:ring-gray-300",
    "hover:ring-black group-data-[invalid=true]:hover:ring-danger-dark",
    "group-data-[invalid=true]:ring-danger group-data-[invalid=true]:text-danger",
    "group-data-[disabled=true]:bg-gray-200 group-data-[disabled=true]:text-gray-disabled",
  ],
  label: [
    clsx({ "text-sm": size === "sm" }),
    "text-small text-default-900 font-semibold",
    "group-data-[required=true]:after:text-gray-900",
    "group-data-[invalid=true]:text-gray-900",
  ],
  segment: [
    "group first:-ml-0.5 not-first:-ml-1 px-0.5 my-auto box-content tabular-nums text-start",
    "inline-block outline-hidden focus:shadow-sm rounded-md",
    "text-gray-600",
    "data-[editable=true]:text-gray-900!",
    "data-[editable=true]:data-[placeholder=true]:text-gray-600!",
    "data-[invalid=true]:text-gray-900 data-[invalid=true]:data-[editable=true]:text-gray-900",
    "data-[invalid=true]:focus:bg-danger-400/50 dark:data-[invalid=true]:focus:bg-danger-400/20",
    "data-[invalid=true]:data-[editable=true]:focus:text-danger",
    "data-[type=literal]:text-gray-600",
    "data-[editable=true][data-type=literal]:!text-gray-600",
  ],
});

export function getDatePickerClassNames(
  size?: "sm" | "md" | "lg",
): DatePickerProps["classNames"] {
  const dateInputClassNames = getDateInputClassNames(size);

  return {
    ...dateInputClassNames,
  };
}

export function getCalendarClassNames({
  isRange,
}: {
  isRange?: boolean;
} = {}): CalendarProps["classNames"] {
  return {
    base: "bg-white",
    cellButton: clsx([
      "rounded-md",
      "data-[disabled=true]:text-gray-600",
      "data-[unavailable=true]:text-gray-600",
      !isRange && [
        "data-[hover=true]:border",
        "data-[hover=true]:border-primary",
        "data-[hover=true]:bg-primary-light",
        "data-[hover=true]:text-primary",
      ],
      isRange && [
        // middle
        "data-[selected=true]:data-[range-selection=true]:before:bg-primary-50",
        "data-[selected=true]:data-[range-selection=true]:text-primary",
        "data-[selected=true]:data-[range-selection=true]:bg-primary-light",
        "data-[selected=true]:data-[range-selection=true]:rounded-none",
        // start (selected)
        "data-[selected=true]:data-[selection-start=true]:data-[range-selection=true]:bg-primary",
        "data-[selected=true]:data-[selection-start=true]:data-[range-selection=true]:rounded-md",
        "data-[selected=true]:data-[selection-start=true]:data-[range-selection=true]:text-primary-foreground",
        // end (selected)
        "data-[selected=true]:data-[selection-end=true]:data-[range-selection=true]:bg-primary",
        "data-[selected=true]:data-[selection-end=true]:data-[range-selection=true]:rounded-md",
        "data-[selected=true]:data-[selection-end=true]:data-[range-selection=true]:text-primary-foreground",
      ],
    ]),
    content: "bg-white",
    errorMessage: "text-danger-dark",
    gridBody: "bg-white",
    gridHeaderCell: "text-gray-900",
    header: "bg-white",
    headerWrapper: "bg-white",
    nextButton: [
      "rounded-md",
      "text-gray-900",
      "data-[hover=true]:bg-primary-light data-[hover=true]:text-primary data-[hover=true]:border-primary data-[hover=true]:border",
    ],
    pickerItem: "text-md",
    prevButton: [
      "rounded-md",
      "text-gray-900",
      "data-[hover=true]:bg-primary-light data-[hover=true]:text-primary data-[hover=true]:border-primary data-[hover=true]:border",
    ],
    title: "text-gray-900",
  };
}
